import PropTypes from 'prop-types';

import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { approveStockAdjustment } from '#/api/imsMgmt/receipts/stockAdjustment/approve';
import { cancelStockAdjustment } from '#/api/imsMgmt/receipts/stockAdjustment/cancel';
import { fetchStockAdjustmentDetail } from '#/api/imsMgmt/receipts/stockAdjustment/fetchDetail';
import { rejectStockAdjustment } from '#/api/imsMgmt/receipts/stockAdjustment/reject';
import { submitStockAdjustment } from '#/api/imsMgmt/receipts/stockAdjustment/submit';
import SideMsg from '#/modules/commonComponents/SideMsg';
import DocumentSideMsgActions from '#/modules/imsMgmt/receipts/shared/DocumentSideMsgActions';
import { useDocumentSideMsgWorkflow } from '#/modules/imsMgmt/receipts/shared/useDocumentSideMsgWorkflow.jsx';
import {
  stockAdjustmentSideMsgDetailBlock,
  stockAdjustmentSideMsgStatusBlock,
} from '#/modules/imsMgmt/receipts/stockAdjustment/data/stockAdjustmentSideMsgConfig';
import {
  getStockAdjustmentApiErrorMessage,
  isStockAdjustmentVersionConflict,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/apiError';
import {
  canApproveStockAdjustment,
  canCancelStockAdjustment,
  canRejectStockAdjustment,
  canSubmitStockAdjustment,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/adjustmentStatus';
import {
  stockAdjustmentDetailQueryKey,
  stockAdjustmentListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CONFIRM_COPY = {
  submit: {
    title: '提交調整單',
    message: '提交後將鎖定相關庫存並進入待核准，確定提交？',
    confirmText: '提交',
  },
  approve: {
    title: '核准調整單',
    message: '核准後將更新庫存數量，確定核准？',
    confirmText: '核准',
  },
  reject: {
    title: '退回調整單',
    message: '退回後將恢復為草稿並釋放鎖定，確定退回？',
    confirmText: '退回',
  },
  cancel: {
    title: '刪除草稿',
    message: '刪除後無法復原，確定刪除？',
    confirmText: '刪除',
  },
};

const APIS = {
  submit: submitStockAdjustment,
  approve: approveStockAdjustment,
  reject: rejectStockAdjustment,
  cancel: cancelStockAdjustment,
  fetchDetail: fetchStockAdjustmentDetail,
};

const StockAdjustmentSideMsg = ({ displayData, setSelectedData }) => {
  const canCreate = useCan(PERM.STOCK_ADJUSTMENT_CREATE);
  const canReview = useCan(PERM.STOCK_ADJUSTMENT_REVIEW);

  const workflow = useDocumentSideMsgWorkflow({
    row: displayData,
    setSelectedData,
    confirmCopy: CONFIRM_COPY,
    canSubmit: (status) => canCreate && canSubmitStockAdjustment(status),
    canApprove: (status) => canReview && canApproveStockAdjustment(status),
    canReject: (status) => canReview && canRejectStockAdjustment(status),
    canCancel: (status) => canCreate && canCancelStockAdjustment(status),
    apis: APIS,
    listQueryKeyPrefix: stockAdjustmentListQueryKeyPrefix,
    detailQueryKey: stockAdjustmentDetailQueryKey,
    getErrorMessage: getStockAdjustmentApiErrorMessage,
    isVersionConflict: isStockAdjustmentVersionConflict,
  });

  const hasActions = (
    workflow.canSubmit
    || workflow.canApprove
    || workflow.canReject
    || workflow.canCancel
  );

  const blocks = [
    stockAdjustmentSideMsgStatusBlock,
    {
      condition: () => hasActions,
      fields: [
        {
          key: 'actions',
          label: '操作',
          render: () => (
            <div className="side_msg-row">
              <h3>操作</h3>
              <DocumentSideMsgActions
                canSubmit={workflow.canSubmit}
                canApprove={workflow.canApprove}
                canReject={workflow.canReject}
                canCancel={workflow.canCancel}
                isPending={workflow.isPending}
                onSubmit={workflow.handleSubmit}
                onApprove={workflow.handleApprove}
                onReject={workflow.handleReject}
                onCancel={workflow.handleCancel}
              />
            </div>
          ),
        },
      ],
    },
    stockAdjustmentSideMsgDetailBlock,
  ];

  return (
    <>
      {workflow.ConfirmDialogHost}
      <workflow.RejectDialogHost />
      <SideMsg
        title="調整單"
        titleValue="adjustmentNo"
        displayData={displayData}
        blocks={blocks}
      />
    </>
  );
};

StockAdjustmentSideMsg.propTypes = {
  displayData: PropTypes.object,
  setSelectedData: PropTypes.func.isRequired,
};

export default StockAdjustmentSideMsg;
